"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { coordKey } from "../../hex-path";
import type { HexCoord } from "../../hex-path/types";
import { resolvePlayerPathCell } from "../layout-engine";
import { HEX_PATH_TILE_TRAIL_SCALE_OUT_MS } from "../lib/hex-grid-hover-dom";
import { findPathTileRoute } from "../lib/path-character-movement";
import type { MapLayout, ModuleMapGraph } from "../map-graph-types";

const EMPTY_KEYS = new Set<string>();

interface UseScopedPanelSpotlightPathKeysOptions {
	spotlightStopId: string | null;
	activePlayerCoord: HexCoord | null;
	resolvePathOriginCoord: () => HexCoord | null;
	graph: ModuleMapGraph;
	displayLayout: MapLayout;
	trailExitMs?: number;
}

export interface ScopedPanelSpotlightPathTileState {
	key: string;
	exitFromCharacter: boolean;
}

export interface UseScopedPanelSpotlightPathKeysResult {
	/** Route tiles still ahead of / under the character (shrinks while walking). */
	activeRouteCoordKeys: ReadonlySet<string>;
	/** Active route plus tiles playing the 600ms spotlight exit (stay above dim). */
	renderCoordKeys: ReadonlySet<string>;
	/** Tiles currently in the exit animation (for styling). */
	exitingTiles: ReadonlyMap<string, ScopedPanelSpotlightPathTileState>;
}

export function useScopedPanelSpotlightPathKeys(
	options: UseScopedPanelSpotlightPathKeysOptions,
): UseScopedPanelSpotlightPathKeysResult {
	const {
		spotlightStopId,
		activePlayerCoord,
		resolvePathOriginCoord,
		graph,
		displayLayout,
		trailExitMs = HEX_PATH_TILE_TRAIL_SCALE_OUT_MS,
	} = options;

	const [exitingTiles, setExitingTiles] = useState<
		ReadonlyMap<string, ScopedPanelSpotlightPathTileState>
	>(() => new Map());
	const exitTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
		new Map(),
	);
	const prevActiveRouteKeysRef = useRef<Set<string>>(new Set());
	const prevCharacterCoordKeyRef = useRef<string | null>(null);
	const previousSpotlightStopIdRef = useRef<string | null>(null);

	const scheduleSpotlightTileExit = useCallback(
		(entries: Array<{ key: string; exitFromCharacter: boolean }>) => {
			if (entries.length === 0) {
				return;
			}
			setExitingTiles((current) => {
				const next = new Map(current);
				for (const entry of entries) {
					next.set(entry.key, {
						key: entry.key,
						exitFromCharacter: entry.exitFromCharacter,
					});
				}
				return next;
			});

			for (const entry of entries) {
				const existing = exitTimersRef.current.get(entry.key);
				if (existing) {
					clearTimeout(existing);
				}
				const timer = setTimeout(() => {
					exitTimersRef.current.delete(entry.key);
					setExitingTiles((current) => {
						if (!current.has(entry.key)) {
							return current;
						}
						const next = new Map(current);
						next.delete(entry.key);
						return next;
					});
				}, trailExitMs);
				exitTimersRef.current.set(entry.key, timer);
			}
		},
		[trailExitMs],
	);

	const clearAllSpotlightExits = useCallback(() => {
		for (const timer of exitTimersRef.current.values()) {
			clearTimeout(timer);
		}
		exitTimersRef.current.clear();
		prevActiveRouteKeysRef.current = new Set();
		prevCharacterCoordKeyRef.current = null;
		setExitingTiles(new Map());
	}, []);

	const activeRouteCoordKeys = useMemo(() => {
		if (!spotlightStopId) {
			return EMPTY_KEYS;
		}
		const targetCell = resolvePlayerPathCell(
			spotlightStopId,
			graph,
			displayLayout,
		);
		if (!targetCell) {
			return EMPTY_KEYS;
		}
		const originCoord = resolvePathOriginCoord() ?? activePlayerCoord;
		if (!originCoord) {
			return new Set([coordKey(targetCell.row, targetCell.col)]);
		}
		const route = findPathTileRoute(
			originCoord,
			{ row: targetCell.row, col: targetCell.col },
			displayLayout.pathCoordSet,
		);
		if (!route?.length) {
			return new Set([coordKey(targetCell.row, targetCell.col)]);
		}
		return new Set(route.map((step) => coordKey(step.row, step.col)));
	}, [
		activePlayerCoord,
		displayLayout,
		graph,
		resolvePathOriginCoord,
		spotlightStopId,
	]);

	useEffect(() => {
		if (previousSpotlightStopIdRef.current === spotlightStopId) {
			return;
		}
		const previousStopId = previousSpotlightStopIdRef.current;
		previousSpotlightStopIdRef.current = spotlightStopId;

		if (spotlightStopId == null) {
			clearAllSpotlightExits();
			return;
		}

		if (previousStopId != null) {
			const prevActive = prevActiveRouteKeysRef.current;
			const hopExits: Array<{ key: string; exitFromCharacter: boolean }> =
				[];
			for (const key of prevActive) {
				if (!activeRouteCoordKeys.has(key)) {
					hopExits.push({ key, exitFromCharacter: false });
				}
			}
			scheduleSpotlightTileExit(hopExits);
		}

		prevActiveRouteKeysRef.current = new Set(activeRouteCoordKeys);
		prevCharacterCoordKeyRef.current = activePlayerCoord
			? coordKey(activePlayerCoord.row, activePlayerCoord.col)
			: null;
	}, [
		activePlayerCoord,
		activeRouteCoordKeys,
		clearAllSpotlightExits,
		scheduleSpotlightTileExit,
		spotlightStopId,
	]);

	useEffect(() => {
		if (!spotlightStopId) {
			return;
		}

		const prevActive = prevActiveRouteKeysRef.current;
		const characterKey = prevCharacterCoordKeyRef.current;
		const newlyExiting: Array<{ key: string; exitFromCharacter: boolean }> =
			[];

		for (const key of prevActive) {
			if (!activeRouteCoordKeys.has(key)) {
				newlyExiting.push({
					key,
					exitFromCharacter: key === characterKey,
				});
			}
		}

		if (newlyExiting.length > 0) {
			scheduleSpotlightTileExit(newlyExiting);
		}

		prevActiveRouteKeysRef.current = new Set(activeRouteCoordKeys);
		prevCharacterCoordKeyRef.current = activePlayerCoord
			? coordKey(activePlayerCoord.row, activePlayerCoord.col)
			: null;
	}, [
		activePlayerCoord,
		activeRouteCoordKeys,
		scheduleSpotlightTileExit,
		spotlightStopId,
	]);

	useEffect(() => {
		return () => {
			for (const timer of exitTimersRef.current.values()) {
				clearTimeout(timer);
			}
			exitTimersRef.current.clear();
		};
	}, []);

	const renderCoordKeys = useMemo(() => {
		if (exitingTiles.size === 0) {
			return activeRouteCoordKeys;
		}
		const merged = new Set(activeRouteCoordKeys);
		for (const key of exitingTiles.keys()) {
			merged.add(key);
		}
		return merged;
	}, [activeRouteCoordKeys, exitingTiles]);

	return {
		activeRouteCoordKeys,
		renderCoordKeys,
		exitingTiles,
	};
}
